import { getAccountStore } from '../account/accountStore.ts';
import { isRoomCode, normalizeRoomCode } from '@il-colpo/protocol';
import {
  loadApp,
  loadBrowser,
  loadDeepLink,
  loadHaptics,
  loadOpener,
  loadShare,
  loadSplash,
  loadStatusBar,
} from './plugins.ts';
import { IN_CAPACITOR, IN_TAURI } from './shell.ts';

/**
 * What the app does differently inside a native shell. Capacitor (the iOS and Android apps): the system bars and
 * the splash, the back button of Android, the vibration of the device. Both, and Tauri (the desktop app) with
 * them: the system browser for the sign-in of a provider and the way back from it
 * (`ilcolpo://auth/callback?code=…`), and the links that bring somebody to a table. Every function does nothing
 * (or the web's own thing) in the web build, and the plugins are loaded only here, when the shell asks.
 */

const BACKGROUND = '#0A0A18';

/** Opens an address in the system browser (a Custom Tab on Android, an in-app Safari sheet on iOS, the browser of the desktop). */
export async function openExternal(url: string): Promise<void> {
  if (IN_TAURI) {
    const { openUrl } = await loadOpener();
    await openUrl(url);
    return;
  }
  if (!IN_CAPACITOR) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  const { Browser } = await loadBrowser();
  await Browser.open({ url });
}

/** Opens the share sheet of the system; false when there is none (the web app copies instead). */
export async function shareText(args: {
  title: string;
  text: string;
  url: string;
}): Promise<boolean> {
  if (!IN_CAPACITOR) return false;
  const { Share } = await loadShare();
  try {
    await Share.share({ ...args, dialogTitle: args.title });
  } catch {
    // The sheet was closed without choosing: not an error.
  }
  return true;
}

// Loaded once, at the first buzz: the sounds that buzz come many in a second.
let haptics: ReturnType<typeof loadHaptics> | null = null;

/** A buzz of the device, from a pattern of milliseconds (buzz, pause, buzz…). */
export function nativeBuzz(pattern: readonly number[]): void {
  if (!IN_CAPACITOR) return;
  haptics ??= loadHaptics();
  void haptics.then(({ Haptics, ImpactStyle }) => {
    // The patterns are short taps and rumbles; a native impact is the same gesture on both systems.
    const first = pattern[0] ?? 0;
    const style =
      first >= 40 ? ImpactStyle.Heavy : first >= 20 ? ImpactStyle.Medium : ImpactStyle.Light;
    void Haptics.impact({ style });
  });
}

/** Moves the router without a handle on it: BrowserRouter listens to `popstate`. */
function go(path: string): void {
  history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Where a link of the app leads inside it: `ilcolpo://auth/callback?code=…` is a sign-in coming back. */
export async function handleAppUrl(url: string): Promise<void> {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return;
  }
  if (parsed.protocol !== 'ilcolpo:') return;
  if (parsed.hostname === 'join') {
    // An invitation: `ilcolpo://join/K7M2QX` goes to the table, where the code is checked again.
    const code = normalizeRoomCode(parsed.pathname.replace(/^\/+/, ''));
    if (isRoomCode(code)) go(`/online/join/${code}`);
    return;
  }
  if (parsed.hostname !== 'auth') return;
  const client = await getAccountStore().getState().ensure();
  const done = client ? await client.handleAuthUrl(url) : false;
  // The system browser that showed the provider is still on top of the app (iOS keeps it until closed).
  try {
    const { Browser } = await loadBrowser();
    await Browser.close();
  } catch {
    // Android's Custom Tab goes away by itself; there is nothing to close.
  }
  go(done ? '/account' : '/auth/callback?error=access_denied');
}

/** What the Android back button does: pause in a heist, go back, or leave from the first page. */
export function backAction(path: string, canGoBack: boolean): 'escape' | 'back' | 'exit' {
  // In a heist or a training run the key that pauses is the way out, the same as on a keyboard.
  if (path === '/game' || path === '/training') return 'escape';
  if (path === '/' || !canGoBack) return 'exit';
  return 'back';
}

const pressEscape = (): void => {
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }),
  );
};

let started = false;

/** The desktop app: the links the system opens the app with (at its start, and while it runs). */
async function startTauri(): Promise<void> {
  const { getCurrent, onOpenUrl } = await loadDeepLink();
  // A link that started the app is waiting; one that comes later is announced.
  for (const url of (await getCurrent()) ?? []) void handleAppUrl(url);
  await onOpenUrl((urls) => {
    for (const url of urls) void handleAppUrl(url);
  });
}

export async function startNative(): Promise<void> {
  if (started) return;
  if (IN_TAURI) {
    started = true;
    await startTauri();
    return;
  }
  if (!IN_CAPACITOR) return;
  started = true;
  const [{ App }, { StatusBar, Style }, { SplashScreen }] = await Promise.all([
    loadApp(),
    loadStatusBar(),
    loadSplash(),
  ]);
  // Light icons on bars of the colour of the page behind them.
  await StatusBar.setStyle({ style: Style.Dark }).catch(() => undefined);
  await StatusBar.setBackgroundColor({ color: BACKGROUND }).catch(() => undefined);
  await App.addListener('appUrlOpen', ({ url }) => {
    void handleAppUrl(url);
  });
  await App.addListener('backButton', ({ canGoBack }) => {
    // An open dialog closes first (the dialogs listen for Escape, as the gamepad's B button does).
    if (document.querySelector('[role="dialog"]')) {
      pressEscape();
      return;
    }
    const action = backAction(location.pathname, canGoBack);
    if (action === 'escape') pressEscape();
    else if (action === 'back') history.back();
    else void App.exitApp();
  });
  // The first screen is drawn behind the native splash: show it now.
  await SplashScreen.hide({ fadeOutDuration: 200 });
}
